import React, { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Loader, SwapResolvedMessage } from '../../../components';
import { NFTSwapContainer } from '../../../components/SwapKiwi/NFTSwapContainer';
import {
  selectAppState,
  selectEnteredEthAmount,
  selectSelectedNFTs,
  selectSwapResolvedData,
} from '../../../ducks/app/selectors';
import { AnswerSwapStep, AppState } from '../../../ducks/app/slice';
import { useSwap } from '../../../hooks/apollo/useSwap';
import { useWeb3React } from '@web3-react/core';
import { setActiveSwap, storeAnswerSwapStep, storeAppState } from '../../../ducks/app/actions';
import { INFT } from '../../../types/nft';
import { SwapStatus } from '../../../types/enums';
import { useVerifiedNFTsList } from '../../../hooks/useVerifiedNFTsList';
import { getAreNFTsVerified } from '../../../utils/nft';
import { BadgeSizes, SKBadge } from '../../../components/SwapKiwi/Badge';
import { IconComp } from '../../../components/SwapKiwi/IconComponent';
import { ReactComponent as Refresh } from '../../../assets/svg/icons/reload.svg';
import { Routes } from '../../../constants/routes';
import { PageHeader } from '../../../components/SwapKiwi/PageHeader';
import { ReactComponent as CursorKiwiStrike } from '../../../assets/svg/icons/cursor-kiwi-strike.svg';
import { NoDataContainer } from '../../../components/SwapKiwi/NoDataContainer';

export const RecentSwapDetails: FC = () => {
  const appState = useSelector(selectAppState);
  const selectedNFTs = useSelector(selectSelectedNFTs);
  const { type, message, annotations } = useSelector(selectSwapResolvedData);
  const enteredEthAmount = useSelector(selectEnteredEthAmount);
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const { loading, data } = useSwap(id);
  const { account } = useWeb3React();
  const { data: verifiedNFTAddresses } = useVerifiedNFTsList();
  useEffect(() => {
    if (data?.status === SwapStatus.PROPOSED) {
      dispatch(storeAppState(AppState.ANSWER_SWAP));
    }

    if (data?.status === SwapStatus.INITIATED && account?.toUpperCase() === data.fromUser.toUpperCase()) {
      dispatch(storeAppState(AppState.ANSWER_SWAP));
      dispatch(storeAnswerSwapStep(AnswerSwapStep.FINAL_SWAP_CONFIRMATION));
    }
    const isResolved =
      data?.status === SwapStatus.EXECUTED ||
      data?.status === SwapStatus.CANCELLED ||
      data?.status === SwapStatus.REJECTED;
    if (isResolved && message) dispatch(storeAppState(AppState.SWAP_RESOLVED));

    // store swap to appState
    if (data) {
      // TODO: this is only ran once, and doesn't contain the latest version of the swap data
      dispatch(setActiveSwap(data));
    }
  }, [data?.userNFTs.length, data?.status]);

  const getUserNfts = useMemo((): INFT[] => {
    if (selectedNFTs.length) {
      return getAreNFTsVerified(selectedNFTs, verifiedNFTAddresses);
    } else {
      return data ? getAreNFTsVerified(data.userNFTs, verifiedNFTAddresses) : [];
    }
  }, [selectedNFTs, data?.userNFTs, verifiedNFTAddresses]);

  const getUserEthAmount = (): string | undefined => enteredEthAmount?.toString() ?? data?.userEth;

  const getOfferedNfts = useMemo(
    (): INFT[] => (data ? getAreNFTsVerified(data.offeredNFTs, verifiedNFTAddresses) : []),
    [data?.offeredNFTs, verifiedNFTAddresses],
  );

  if (appState === AppState.SWAP_RESOLVED) {
    return <SwapResolvedMessage type={type} message={message} annotations={annotations} />;
  }

  if (loading) {
    return <Loader />;
  }

  const isAuthorized = account?.toLowerCase() === data?.fromUser || account?.toLowerCase() === data?.toUser;

  if ((!loading && !data) || !isAuthorized) {
    return (
      <NoDataContainer
        color="grape"
        message1="Oops, swap not found."
        hasCta
        description="An error occured or this page doesn't exist."
      />
    );
  }
  const userEthAmount = getUserEthAmount();
  return (
    <section>
      <PageHeader
        background="grape-400"
        title="Recent Swap Details"
        description='<p class="text-lg text-cloud-900">Lorem ipsum dolor sit amet, consectetur adipiscing elit.<br> Diam turpis leo, nulla massa ultricies lectus fringilla.</p>'
        link={{ label: 'Learn More', url: '/' }}
        sticker={CursorKiwiStrike}
        stickerWidth={264}
        breadcrumbs={{
          links: [
            {
              label: 'Home',
              slug: Routes.LANDING_ROUTE,
            },
            {
              label: 'Recent Swaps',
              slug: Routes.RECENT_SWAPS,
            },
            {
              label: location.pathname.split(`${Routes.RECENT_SWAPS}/`)[1],
              slug: Routes.RECENT_SWAP_DETAILS,
            },
          ],
        }}
        notTopBorder={false}
      />
      <div className="flex flex-wrap justify-between items-start app-container mx-auto py-9">
        <div className="user-nft-container w-46%">
          <div className="flex gap-1 items-center min-h-4 mb-4 border-b border-cloud-900">
            <span className="text-display-sm mr-2">Your Offer</span>
            <SKBadge size={BadgeSizes.LG} color="outline">
              {userEthAmount ? userEthAmount : 0}
              <span>ETH</span>
            </SKBadge>
            <SKBadge size={BadgeSizes.LG} color="outline">
              {data?.userNFTs.length}
              <span>NFTs</span>
            </SKBadge>
          </div>
          <NFTSwapContainer NFTs={getUserNfts} convertToGridOnXL isTransferring />
        </div>
        <div className="bg-cloud-900 w-3 h-3 mt-5 rounded-full flex items-center justify-center">
          <IconComp icon={Refresh} size={30} color="white" className="stroke-current text-onion-50" />
        </div>
        <div className="user-nft-container w-46%">
          <>
            <div className="flex gap-1 items-center min-h-4 pb-2 mb-4 border-b border-cloud-900">
              <span className="text-display-sm mr-2">Offered NFTs</span>
              <SKBadge size={BadgeSizes.LG} color="outline">
                {data?.offeredEth ? data?.offeredEth : 0}
                <span>ETH</span>
              </SKBadge>
              <SKBadge size={BadgeSizes.LG} color="outline">
                {data?.offeredNFTs.length}
                <span>NFTs</span>
              </SKBadge>
            </div>
            <NFTSwapContainer NFTs={getOfferedNfts} convertToGridOnXL />
          </>
        </div>
      </div>
    </section>
  );
};
